import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { MapContainer, TileLayer, ImageOverlay, Marker, Popup, useMap, useMapEvents } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-cluster'
import toast from 'react-hot-toast'
import { redApi } from '../../services/api'
import { FASI_IMMOBILE, FASE_COLORI } from '../../constants/red'
import { MapPin, Layers, RefreshCw } from 'lucide-react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'

// Fix default Leaflet icons
const DefaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
})
L.Marker.prototype.options.icon = DefaultIcon

interface MappaImmobile {
  id: string
  numero_progressivo?: number
  nome_riferimento: string
  indirizzo: string
  comune: string
  provincia: string
  cap?: string
  foglio?: string
  particella?: string
  subalterno?: string
  fase_corrente: string
  lat: number
  lon: number
  prezzo_acquisto: number | null
  pratica_usr?: {
    numero_pratica?: string
    importo_approvato?: number | null
  }
}

// WMS pubblico e gratuito dell'Agenzia delle Entrate (CC BY 4.0) — confini
// reali delle particelle catastali sovrapposti alla mappa
const WMS_CATASTO_URL = 'https://wms.cartografia.agenziaentrate.gov.it/inspire/wms/ows01.php'

// Molti immobili in fase di scouting hanno solo l'indirizzo di una frazione
// rurale: il geocoder non riesce a risolverlo a livello di civico e ripiega
// sul centro del comune, per cui più immobili finiscono con le stesse
// coordinate esatte e i loro marker si sovrappongono (invisibili tranne il
// primo). Qui li disponiamo su un piccolo cerchio attorno al punto comune,
// così restano tutti visibili e cliccabili singolarmente — lo zoom automatico
// del cluster (sotto) si occupa poi di raggrupparli quando la mappa è troppo
// lontana per distinguerli.
function calcolaPosizioniDisperse(immobili: MappaImmobile[]): Record<string, [number, number]> {
  const gruppi: Record<string, MappaImmobile[]> = {}
  immobili.forEach(i => {
    const chiave = `${i.lat.toFixed(5)},${i.lon.toFixed(5)}`
    ;(gruppi[chiave] ||= []).push(i)
  })
  const risultato: Record<string, [number, number]> = {}
  Object.values(gruppi).forEach(gruppo => {
    if (gruppo.length === 1) {
      risultato[gruppo[0].id] = [gruppo[0].lat, gruppo[0].lon]
      return
    }
    const [lat0, lon0] = [gruppo[0].lat, gruppo[0].lon]
    const latRad = (lat0 * Math.PI) / 180
    const raggioMetri = 35 + gruppo.length * 5
    const raggioGradiLat = raggioMetri / 111320
    const raggioGradiLon = raggioMetri / (111320 * Math.cos(latRad))
    gruppo.forEach((imm, idx) => {
      const angolo = (2 * Math.PI * idx) / gruppo.length
      risultato[imm.id] = [
        lat0 + raggioGradiLat * Math.sin(angolo),
        lon0 + raggioGradiLon * Math.cos(angolo),
      ]
    })
  })
  return risultato
}

function iconCluster(cluster: { getChildCount: () => number }) {
  const count = cluster.getChildCount()
  return L.divIcon({
    html: `<div style="
      width:38px;height:38px;border-radius:50%;
      background:#1E40AF;color:#fff;
      display:flex;align-items:center;justify-content:center;
      font-size:13px;font-weight:700;
      border:3px solid #fff;
      box-shadow:0 2px 8px rgba(0,0,0,0.3);
    ">${count}</div>`,
    className: 'custom-cluster',
    iconSize: L.point(38, 38, true),
  })
}

function FitBounds({ markers }: { markers: [number, number][] }) {
  const map = useMap()
  useEffect(() => {
    if (markers.length > 0) {
      const bounds = L.latLngBounds(markers.map(([lat, lon]) => L.latLng(lat, lon)))
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 })
    }
  }, [map, markers])
  return null
}

// Overlay confini catastali. Il WMS dell'Agenzia delle Entrate espone il
// layer CP.CadastralParcel solo in EPSG:4258 (nessun 3857/4326 — verificato
// via GetCapabilities e richieste dirette), incompatibile con la griglia di
// tile EPSG:3857 usata da Leaflet/OpenStreetMap. Un WMSTileLayer standard
// fallirebbe silenziosamente (ServiceException "InvalidFormat" per ogni
// tile). Soluzione: una singola immagine GetMap per il viewport corrente,
// ricalcolata su pan/zoom — EPSG:4258 (ETRS89) è numericamente equivalente
// a WGS84 su scala di un singolo immobile, quindi i bounds lat/lon di
// Leaflet si possono riusare direttamente per il posizionamento.
//
// Ogni sotto-layer del servizio ha un proprio MaxScaleDenominator (verificato
// via GetCapabilities) che ne limita la visibilità a una certa soglia di
// zoom, oltre la quale il GetMap risponde 200 ma con immagine vuota:
//   - CP.CadastralZoning ("Mappe", i fogli)  → MaxScale 200000 → zoom >= 11
//   - CP.CadastralParcel ("Particelle")      → MaxScale   5000 → zoom >= 17
//   - codice_plla (numero particella, dal gruppo "vestizioni") → MaxScale 2000 → zoom >= 18
// Prima veniva richiesto solo CP.CadastralParcel con soglia unica zoom>=17:
// questo nascondeva del tutto i confini dei fogli (visibili molto prima,
// da zoom 11) e non mostrava mai la numerazione (che il servizio espone
// solo tramite il layer dedicato codice_plla, non via GetFeatureInfo).
// Ora i layer richiesti vengono composti in base allo zoom corrente.
const ZOOM_MIN_FOGLI = 11
const ZOOM_MIN_PARTICELLE = 17
const ZOOM_MIN_NUMERAZIONE = 18

function layersPerZoom(zoom: number): string[] {
  const layers: string[] = []
  if (zoom >= ZOOM_MIN_FOGLI) layers.push('CP.CadastralZoning')
  if (zoom >= ZOOM_MIN_PARTICELLE) layers.push('CP.CadastralParcel')
  if (zoom >= ZOOM_MIN_NUMERAZIONE) layers.push('codice_plla')
  return layers
}

function CatastoOverlay() {
  const map = useMap()
  const [overlay, setOverlay] = useState<{ url: string; bounds: L.LatLngBoundsLiteral } | null>(null)

  const aggiorna = useCallback(() => {
    const layers = layersPerZoom(map.getZoom())
    if (layers.length === 0) {
      setOverlay(null)
      return
    }
    const bounds = map.getBounds()
    const size = map.getSize()
    const sw = bounds.getSouthWest()
    const ne = bounds.getNorthEast()
    const params = new URLSearchParams({
      service: 'WMS',
      request: 'GetMap',
      version: '1.3.0',
      layers: layers.join(','),
      styles: layers.map(() => '').join(','),
      format: 'image/png',
      transparent: 'true',
      crs: 'EPSG:4258',
      width: String(Math.round(size.x)),
      height: String(Math.round(size.y)),
      // WMS 1.3.0 + EPSG:4258 usa ordine assi lat,lon (verificato)
      bbox: [sw.lat, sw.lng, ne.lat, ne.lng].join(','),
    })
    setOverlay({
      url: `${WMS_CATASTO_URL}?${params.toString()}`,
      bounds: [[sw.lat, sw.lng], [ne.lat, ne.lng]],
    })
  }, [map])

  useEffect(aggiorna, [aggiorna])
  useMapEvents({ moveend: aggiorna, zoomend: aggiorna })

  if (!overlay) return null
  return <ImageOverlay url={overlay.url} bounds={overlay.bounds} opacity={0.65} />
}

const RedMappaPortfolio: React.FC = () => {
  const navigate = useNavigate()
  const [immobili, setImmobili] = useState<MappaImmobile[]>([])
  const [totaleImmobili, setTotaleImmobili] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [geocodificando, setGeocodificando] = useState(false)
  const [faseFilter, setFaseFilter] = useState<string>('')
  const [mostraCatasto, setMostraCatasto] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const markerRefs = useRef<Record<string, L.Marker>>({})
  const clusterRef = useRef<L.MarkerClusterGroup | null>(null)

  const load = () => {
    redApi.mappa.immobili(faseFilter || undefined)
      .then(r => setImmobili(r.data))
      .catch(() => setImmobili([]))
      .finally(() => setLoading(false))
  }

  useEffect(load, [faseFilter])

  useEffect(() => {
    redApi.immobili.list().then(r => setTotaleImmobili(r.data.length)).catch(() => {})
  }, [])

  // Il conteggio "mancanti" ha senso solo senza filtro fase attivo, altrimenti
  // immobili.length è già ridotto dal filtro e il conto risulterebbe fuorviante
  const mancanti = (!faseFilter && totaleImmobili != null) ? Math.max(totaleImmobili - immobili.length, 0) : null

  const handleGeocodificaMancanti = async () => {
    setGeocodificando(true)
    try {
      const res = await redApi.immobili.geocodeMissing()
      toast.success(res.data.messaggio || 'Geocodifica avviata')
      // La geocodifica gira in background lato server (chiamate Nominatim,
      // ~1 richiesta/sec): attende qualche secondo poi ricarica la mappa
      setTimeout(() => { load(); setGeocodificando(false) }, Math.min((mancanti || 1) * 1500, 15000))
    } catch {
      toast.error('Errore avvio geocodifica')
      setGeocodificando(false)
    }
  }

  const posizioni = useMemo(() => calcolaPosizioniDisperse(immobili), [immobili])

  const markers = useMemo(() =>
    immobili.map(i => posizioni[i.id] ?? [i.lat, i.lon] as [number, number]),
    [immobili, posizioni]
  )

  const stats = useMemo(() => {
    const perFase: Record<string, number> = {}
    immobili.forEach(i => { perFase[i.fase_corrente] = (perFase[i.fase_corrente] || 0) + 1 })
    return perFase
  }, [immobili])

  const immobiliOrdinati = useMemo(() =>
    [...immobili].sort((a, b) =>
      (a.comune || '').localeCompare(b.comune || '') || a.nome_riferimento.localeCompare(b.nome_riferimento)
    ),
    [immobili]
  )

  const handleSelectImmobile = (imm: MappaImmobile) => {
    setSelectedId(imm.id)
    const marker = markerRefs.current[imm.id]
    if (marker && clusterRef.current) {
      // zoomToShowLayer espande/zooma il cluster finché il marker non è
      // effettivamente visibile sulla mappa, poi apre il popup
      clusterRef.current.zoomToShowLayer(marker, () => marker.openPopup())
    }
  }

  if (loading) return <div className="p-8 text-gray-400">Caricamento mappa...</div>

  return (
    <div className="p-4 space-y-4 h-[calc(100vh-64px)] flex flex-col">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <MapPin size={22} className="text-blue-600" />
          <h1 className="text-xl font-bold text-gray-900">Mappa Portfolio RED</h1>
          <span className="text-sm text-gray-500">
            {immobili.length}{totaleImmobili != null ? ` / ${totaleImmobili}` : ''} immobili georeferenziati
          </span>
          {!!mancanti && (
            <button
              onClick={handleGeocodificaMancanti}
              disabled={geocodificando}
              className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 flex items-center gap-1.5 hover:bg-amber-100 disabled:opacity-60"
              title="Geolocalizza gli immobili senza coordinate usando l'indirizzo dei Dati generali"
            >
              <RefreshCw size={12} className={geocodificando ? 'animate-spin' : ''} />
              {geocodificando ? 'Geocodifica in corso...' : `${mancanti} senza coordinate — Geocodifica mancanti`}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Layers size={16} className="text-gray-400" />
          <select
            className="input text-sm py-1 px-2"
            value={faseFilter}
            onChange={e => setFaseFilter(e.target.value)}
          >
            <option value="">Tutte le fasi</option>
            {FASI_IMMOBILE.map(f => (
              <option key={f} value={f}>{f.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <button
            onClick={() => setMostraCatasto(v => !v)}
            className={`text-sm px-3 py-1.5 rounded-lg border transition ${
              mostraCatasto ? 'bg-blue-600 text-white border-blue-600' : 'btn-secondary'
            }`}
            title="Sovrapponi i confini delle particelle catastali (Agenzia delle Entrate, dato pubblico)"
          >
            Confini catastali
          </button>
          <button onClick={() => navigate('/red/dashboard')} className="btn-secondary text-sm">
            Dashboard
          </button>
          <button onClick={() => navigate('/red')} className="btn-secondary text-sm">
            Portfolio
          </button>
        </div>
      </div>

      {mostraCatasto && (
        <p className="text-xs text-gray-400 -mt-2">
          Confini catastali: <a href="https://www.agenziaentrate.gov.it" target="_blank" rel="noopener noreferrer" className="hover:underline">Agenzia delle Entrate</a> (CC BY 4.0) — fogli visibili da zoom città/comune, particelle e numerazione zoomando su un singolo edificio
        </p>
      )}

      {/* Stats bar */}
      <div className="flex gap-2 flex-wrap">
        {Object.entries(stats).map(([fase, count]) => (
          <button
            key={fase}
            onClick={() => setFaseFilter(faseFilter === fase ? '' : fase)}
            className={`text-xs px-2.5 py-1 rounded-full border transition ${
              faseFilter === fase ? 'ring-2 ring-offset-1' : ''
            }`}
            style={{
              background: FASE_COLORI[fase]?.bg || '#f3f4f6',
              color: FASE_COLORI[fase]?.text || '#374151',
              borderColor: FASE_COLORI[fase]?.border || '#e5e7eb',
              '--tw-ring-color': FASE_COLORI[fase]?.text,
            } as React.CSSProperties}
          >
            {fase.replace(/_/g, ' ')} · {count}
          </button>
        ))}
      </div>

      <div className="flex-1 flex gap-3 min-h-[400px] overflow-hidden">
      <div className="flex-1 rounded-xl overflow-hidden border border-gray-200">
        <MapContainer
          center={[43.3, 13.5]}
          zoom={8}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {mostraCatasto && <CatastoOverlay />}
          <FitBounds markers={markers} />
          <MarkerClusterGroup
            ref={clusterRef as any}
            iconCreateFunction={iconCluster as any}
            maxClusterRadius={50}
            spiderfyOnMaxZoom
          >
          {immobili.map(imm => {
            const col = FASE_COLORI[imm.fase_corrente]
            const selezionato = imm.id === selectedId
            const pos = posizioni[imm.id] ?? [imm.lat, imm.lon]
            const dim = selezionato ? 36 : 28
            const icon = L.divIcon({
              className: 'custom-marker',
              html: `<div style="
                width:${dim}px;height:${dim}px;border-radius:50%;
                background:${col?.bg || '#e5e7eb'};
                border:${selezionato ? 3 : 2}px solid ${selezionato ? '#1E40AF' : (col?.text || '#6b7280')};
                color:${col?.text || '#374151'};
                display:flex;align-items:center;justify-content:center;
                font-size:${selezionato ? 12 : 11}px;font-weight:700;
                box-shadow:0 2px ${selezionato ? 10 : 6}px rgba(${selezionato ? '30,64,175,0.6' : '0,0,0,0.2'});
              ">${imm.numero_progressivo ?? imm.nome_riferimento.charAt(0).toUpperCase()}</div>`,
              iconSize: [dim, dim],
              iconAnchor: [dim / 2, dim / 2],
            })
            return (
              <Marker
                key={imm.id}
                position={pos}
                icon={icon}
                ref={el => { if (el) markerRefs.current[imm.id] = el }}
                eventHandlers={{ click: () => setSelectedId(imm.id) }}
              >
                <Popup>
                  <div className="space-y-1 min-w-[200px]">
                    <p className="font-semibold text-sm">
                      {imm.numero_progressivo != null && <span className="text-blue-600">N.{imm.numero_progressivo} — </span>}
                      {imm.nome_riferimento}
                    </p>
                    <p className="text-xs text-gray-500">{imm.indirizzo}, {imm.comune} ({imm.provincia})</p>
                    <span
                      className="inline-block text-[10px] px-1.5 py-0.5 rounded-full border"
                      style={{
                        background: col?.bg,
                        color: col?.text,
                        borderColor: col?.border,
                      }}
                    >
                      {imm.fase_corrente.replace(/_/g, ' ')}
                    </span>
                    {imm.prezzo_acquisto != null && (
                      <p className="text-xs text-gray-600">Acquisto: €{Number(imm.prezzo_acquisto).toLocaleString('it-IT')}</p>
                    )}
                    {imm.pratica_usr?.importo_approvato != null && (
                      <p className="text-xs text-blue-600">USR: €{Number(imm.pratica_usr.importo_approvato).toLocaleString('it-IT')}</p>
                    )}
                    {(imm.foglio || imm.particella || imm.subalterno) && (
                      <p className="text-xs text-gray-500">
                        Cat.: {[
                          imm.foglio && `Fg. ${imm.foglio}`,
                          imm.particella && `Part. ${imm.particella}`,
                          imm.subalterno && `Sub. ${imm.subalterno}`,
                        ].filter(Boolean).join(' — ')}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-1">
                      <button
                        onClick={() => navigate(`/red/${imm.id}`)}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Apri dettaglio →
                      </button>
                      <a
                        href="https://catastomappe.it/mappa"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-gray-500 hover:underline"
                        title={`Cerca manualmente: ${imm.comune} (${imm.provincia})${imm.foglio ? `, Foglio ${imm.foglio}` : ''}${imm.particella ? `, Part. ${imm.particella}` : ''} — coordinate ${imm.lat.toFixed(6)}, ${imm.lon.toFixed(6)}`}
                      >
                        Apri in Catasto ↗
                      </a>
                    </div>
                  </div>
                </Popup>
              </Marker>
            )
          })}
          </MarkerClusterGroup>
        </MapContainer>
      </div>

      {/* Legenda / elenco immobili — selezionandone uno la mappa zooma e apre il marker */}
      <div className="w-72 shrink-0 rounded-xl border border-gray-200 bg-white flex flex-col overflow-hidden">
        <div className="px-3 py-2.5 border-b border-gray-200 shrink-0">
          <h2 className="text-sm font-semibold text-gray-800">Elenco immobili</h2>
          <p className="text-xs text-gray-400">Clic per centrare sulla mappa</p>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
          {immobiliOrdinati.map(imm => {
            const col = FASE_COLORI[imm.fase_corrente]
            const selezionato = imm.id === selectedId
            return (
              <button
                key={imm.id}
                onClick={() => handleSelectImmobile(imm)}
                className={`w-full text-left px-3 py-2 flex items-start gap-2 transition ${
                  selezionato ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
              >
                <span
                  className="mt-0.5 shrink-0 w-3 h-3 rounded-full border"
                  style={{ background: col?.bg || '#e5e7eb', borderColor: col?.text || '#6b7280' }}
                />
                <span className="min-w-0">
                  <span className={`block text-xs font-medium truncate ${selezionato ? 'text-blue-700' : 'text-gray-800'}`}>
                    {imm.numero_progressivo != null && `N.${imm.numero_progressivo} — `}{imm.nome_riferimento}
                  </span>
                  <span className="block text-[11px] text-gray-400 truncate">{imm.comune}</span>
                </span>
              </button>
            )
          })}
          {immobiliOrdinati.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-8 px-3">Nessun immobile georeferenziato</p>
          )}
        </div>
      </div>
      </div>
    </div>
  )
}

export default RedMappaPortfolio
